import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { type BuilderContent } from '@builder.io/sdk-angular';
import { environment } from '../../../environments/environment';
import { BuilderService } from '../../services/builder.service';
import { AudienceService } from '../../services/audience.service';
import { HeroSectionComponent } from '../../components/hero-section/hero-section.component';
import { UserCardsComponent } from '../../components/user-cards/user-cards.component';
import { AcademicIntegrityComponent } from '../../components/academic-integrity/academic-integrity.component';
import { LearningScienceComponent } from '../../components/learning-science/learning-science.component';
import { ArticleListingComponent } from '../../components/article-listing/article-listing.component';
import { BrowseByCategoryComponent } from '../../components/browse-by-category/browse-by-category.component';
import { AVAILABLE_TAXONOMY_TYPES } from '../../config/taxonomy.config';

@Component({
  selector: 'app-builder-page',
  templateUrl: './builder-page.component.html',
  styleUrls: ['./builder-page.component.scss'],
  standalone: false
})
export class BuilderPageComponent implements OnInit {
  content: BuilderContent | null = null;
  loading = true;
  error: string | null = null;
  apiKey = environment.builderApiKey;
  audience: string = '';
  customComponents = [
    {
      component: HeroSectionComponent as any,
      name: 'HeroSection',
      inputs: [
        { name: 'title', type: 'string', defaultValue: 'Bring Learning to Life' },
        { name: 'subtitle', type: 'string', defaultValue: 'Fueling Success: Wherever Learning Happens' },
        {
          name: 'description',
          type: 'longText',
          defaultValue:
            'Empower your teaching with digital tools and resources that provide impactful learning experiences and improve student outcomes, all designed to meet the dynamic needs of educators and learners.'
        }
      ]
    },
    {
      component: UserCardsComponent as any,
      name: 'UserCards',
      inputs: [
        {
          name: 'cards',
          type: 'list',
          subFields: [
            { name: 'title', type: 'string' },
            { name: 'description', type: 'longText' },
            {
              name: 'image',
              type: 'string',
              defaultValue:
                'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=560&h=315&q=80',
              helperText: 'Use a valid image URL starting with https://'
            },
            { name: 'buttonText', type: 'string' },
            { name: 'buttonLink', type: 'string' },
            { name: 'hasDropdown', type: 'boolean' },
            {
              name: 'dropdownItems',
              type: 'list',
              subFields: [
                { name: 'label', type: 'string' },
                { name: 'link', type: 'string' }
              ]
            }
          ],
          defaultValue: [
            {
              title: 'Instructors',
              description: 'Explore powerful instructor tools and resources for your discipline.',
              image:
                'https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=560&h=315&q=80',
              buttonText: 'Browse by Discipline',
              buttonLink: 'https://www.macmillanlearning.com/college/us/catalog',
              hasDropdown: true,
              dropdownItems: [
                { label: 'Astronomy', link: 'https://www.macmillanlearning.com/college/us/discipline/Astronomy' },
                { label: 'Biology', link: 'https://www.macmillanlearning.com/college/us/discipline/Biology' },
                { label: 'Chemistry', link: 'https://www.macmillanlearning.com/college/us/discipline/Chemistry' }
              ]
            },
            {
              title: 'Students',
              description: 'Resources and tools to support your unique learning journey.',
              image:
                'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&w=560&h=315&q=80',
              buttonText: 'Student Store',
              buttonLink: 'https://store.macmillanlearning.com/us',
              hasDropdown: false
            },
            {
              title: 'Achieve',
              description: 'Sign in or set up an Achieve account.',
              image:
                'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=560&h=315&q=80',
              buttonText: 'Sign into Achieve',
              buttonLink: 'https://achieve.macmillanlearning.com/start',
              hasDropdown: false
            }
          ]
        }
      ]
    },
    {
      component: AcademicIntegrityComponent as any,
      name: 'AcademicIntegrity',
      inputs: [
        { name: 'title', type: 'string', defaultValue: "Academic integrity isn't optional—it's essential." },
        { name: 'subtitle', type: 'string', defaultValue: 'Achieve + Respondus Integration, Now Available' },
        {
          name: 'description',
          type: 'longText',
          defaultValue:
            'Strong assessments, now with more security. Pair quality assessments with the peace of mind of enhanced exam security.'
        },
        { name: 'ctaText', type: 'string', defaultValue: 'Try Achieve with Respondus' },
        {
          name: 'ctaLink',
          type: 'string',
          defaultValue:
            'https://go.macmillanlearning.com/achieveaccessrequest.html?utm_source=Microsite&utm_medium=Respondus&utm_content=tryachievewithrespondus&utm_id=119600&utm_campaign=MH-AchieveRetention2501'
        },
        {
          name: 'videoEmbedUrl',
          type: 'string',
          defaultValue: 'https://www.youtube.com/embed/dQw4w9WgXcQ?rel=0&modestbranding=1&controls=1&showinfo=0'
        },
        {
          name: 'videoTitle',
          type: 'string',
          defaultValue: 'How to Use Respondus in Achieve | Secure Online Testing Made Simple'
        },
        {
          name: 'placeholderImage',
          type: 'string',
          defaultValue:
            'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=560&h=315&q=80'
        },
        { name: 'showFallback', type: 'boolean', defaultValue: false }
      ]
    },
    {
      component: LearningScienceComponent as any,
      name: 'LearningScience',
      inputs: [
        { name: 'title', type: 'string', defaultValue: 'The Impact of Learning Science' },
        {
          name: 'subtitle',
          type: 'string',
          defaultValue: 'Empower student success with evidence-based product experiences'
        },
        { name: 'ctaText', type: 'string', defaultValue: 'See the Research' },
        { name: 'ctaLink', type: 'string', defaultValue: 'https://www.macmillanlearning.com/college/us/learning-science' },
        {
          name: 'backgroundImage',
          type: 'string',
          defaultValue:
            'https://images.unsplash.com/photo-1523580846011-d3a5bc25702b?auto=format&fit=crop&w=1200&h=600&q=80'
        }
      ]
    },
    {
      component: ArticleListingComponent as any,
      name: 'ArticleListing',
      inputs: [
        { 
          name: 'mode',
          friendlyName: 'Filter Mode / Type',
          type: 'string', 
          enum: [
            { label: 'Topic', value: 'topic' },
            { label: 'Content Series', value: 'content-series' },
            // Future options:
            // { label: 'Subject', value: 'subject' },
            // { label: 'Discipline', value: 'discipline' }
          ],
          defaultValue: 'topic',
          helperText: 'What type of content to filter by',
          onChange: `
            // Clear all selection fields when mode changes
            // This prevents stale values from previous mode interfering with the new mode
            // The component's mapReferenceInputsToFilter() checks if filterId exists
            // and skips mapping if it does, so we must clear it for new references to work
            // Using null (not undefined) ensures Builder.io truly clears the field
            options.set('selectedTopic', null);
            options.set('selectedContentSeries', null);
            options.set('selectedSubject', null);
            options.set('selectedDiscipline', null);
            options.set('filterId', null);  // Must clear so new reference can be mapped
            options.set('filterValue', 'All');
            
            // Force a state update to ensure Builder.io registers the changes
            options.delete('selectedTopic');
            options.delete('selectedContentSeries');
            options.delete('selectedSubject');
            options.delete('selectedDiscipline');
            options.delete('filterId');
          `
        },
        // Topic selection (only shown when mode is 'topic')
        {
          name: 'selectedTopic',
          friendlyName: 'Selected Topic',
          type: 'reference',
          model: 'blog-topic',
          defaultValue: null,
          helperText: 'Select a topic to filter by (leave empty for "All Topics")',
          showIf: `options.get('mode') === 'topic'`
        },
        // Content Series selection (only shown when mode is 'content-series')
        {
          name: 'selectedContentSeries',
          friendlyName: 'Selected Content Series',
          type: 'reference',
          model: 'content-series',
          defaultValue: null,
          helperText: 'Select a content series to filter by (leave empty for "All Series")',
          showIf: `options.get('mode') === 'content-series'`
        },
        // Subject selection (only shown when mode is 'subject')
        {
          name: 'selectedSubject',
          friendlyName: 'Selected Subject',
          type: 'reference',
          model: 'bfwsubject',
          defaultValue: null,
          helperText: 'Select a subject to filter by (leave empty for "All Subjects")',
          showIf: `options.get('mode') === 'subject'`
        },
        // Discipline selection (only shown when mode is 'discipline')
        {
          name: 'selectedDiscipline',
          friendlyName: 'Selected Discipline',
          type: 'reference',
          model: 'mlcourse',
          defaultValue: null,
          helperText: 'Select a discipline to filter by (leave empty for "All Disciplines")',
          showIf: `options.get('mode') === 'discipline'`
        },
        // Hidden fields that will be auto-populated (for backward compatibility)
        { 
          name: 'filterId', 
          type: 'string',
          helperText: 'Auto-populated from selected category (legacy field)',
          advanced: true,
          showIf: 'false'  // Hidden from UI
        },
        { 
          name: 'filterValue', 
          type: 'string',
          defaultValue: 'All',
          helperText: 'Auto-populated from selected category (legacy field)',
          advanced: true,
          showIf: 'false'  // Hidden from UI
        },
        { 
          name: 'basePath', 
          type: 'string', 
          defaultValue: '/content-hub',
          helperText: 'URL base path for article links'
        },
        {
          name: 'cardVariant',
          friendlyName: 'Card Variant',
          type: 'string',
          enum: [
            { label: 'Default', value: 'default' },
            //{ label: 'Compact', value: 'compact' }, for future use
            //{ label: 'Featured', value: 'featured' }, for future use
            //{ label: 'Browse', value: 'browse' } for future use
          ],
          defaultValue: 'default',
          helperText: 'Card layout style'
        },
        // Advanced options
        {
          name: 'listenToFilterEvents',
          type: 'boolean',
          defaultValue: true,
          helperText: 'Listen for filter change events from dropdown selectors',
          advanced: true
        }
      ]
    },
    {
      component: BrowseByCategoryComponent as any,
      name: 'BrowseByCategory',
      inputs: [
        {
          name: 'categoryType',
          friendlyName: 'Category Type',
          type: 'string',
          enum: [
            { label: 'Topic', value: 'topic' },
            { label: 'Content Series', value: 'content-series' },
            //{ label: 'Subject', value: 'subject' }, for future use
            //{ label: 'Discipline', value: 'discipline' } for future use
          ],
          defaultValue: 'topic',
          helperText: 'Type of categories to display',
          onChange: `
            // Clear excluded fields when category type changes
            options.delete('excludedTopics');
            options.delete('excludedContentSeries');
            options.delete('excludedSubjects');
            options.delete('excludedDisciplines');
          `
        },
        // Multi-select exclusion lists (EXCLUDE unwanted categories)
        {
          name: 'excludedTopics',
          friendlyName: 'Excluded Topics',
          type: 'list',
          subFields: [
            {
              name: 'topic',
              type: 'reference',
              model: 'blog-topic'
            }
          ],
          helperText: 'Select topics to HIDE (leave empty to show all topics with articles)',
          showIf: `options.get('categoryType') === 'topic'`
        },
        {
          name: 'excludedContentSeries',
          friendlyName: '"Excluded Content Series"',
          type: 'list',
          subFields: [
            {
              name: 'series',
              friendlyName: 'Content Series',
              type: 'reference',
              model: 'content-series'
            }
          ],
          helperText: 'Select content series to HIDE (leave empty to show all with articles)',
          showIf: `options.get('categoryType') === 'content-series'`
        },
        {
          name: 'excludedSubjects',
          friendlyName: 'Excluded Subjects',
          type: 'list',
          subFields: [
            {
              name: 'subject',
              type: 'reference',
              model: 'bfwsubject'
            }
          ],
          helperText: 'Select subjects to HIDE (leave empty to show all subjects with articles)',
          showIf: `options.get('categoryType') === 'subject'`
        },
        {
          name: 'excludedDisciplines',
          friendlyName: 'Excluded Disciplines',
          type: 'list',
          subFields: [
            {
              name: 'discipline',
              type: 'reference',
              model: 'mlcourse'
            }
          ],
          helperText: 'Select disciplines to HIDE (leave empty to show all with articles)',
          showIf: `options.get('categoryType') === 'discipline'`
        },
        // Display configuration
        {
          name: 'previewCount',
          friendlyName: 'Preview Count',
          type: 'number',
          defaultValue: 3,
          helperText: 'Number of article previews per category (1-5 recommended)'
        },
        {
          name: 'sortBy',
          friendlyName: 'Sort By',
          type: 'string',
          enum: ['alphabetical', 'articleCount'],
          defaultValue: 'alphabetical',
          helperText: 'How to sort categories'
        },
        {
          name: 'basePath',
          friendlyName: 'Base Path',
          type: 'string',
          defaultValue: '/content-hub',
          helperText: 'Base URL path for article links',
          advanced: true
        },
        {
          name: 'viewAllUrlOverrides',
          friendlyName: 'View All URL Overrides',
          type: 'object',
          helperText: 'Custom URL mappings for categories (JSON: {"Category Name": "/url"})',
          advanced: true
        },
        {
          name: 'showFeaturedImageFallback',
          friendlyName: 'Show Fallback Images',
          type: 'boolean',
          defaultValue: true,
          helperText: 'Show fallback images when article has no featured image',
          advanced: true
        }
      ]
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private builderService: BuilderService,
    private audienceService: AudienceService,
    private titleService: Title
  ) {}

  async ngOnInit() {
    try {
      // Get audience from parent route parameter or service
      // For routes like /content-hub/:audience/**, audience is in grandparent
      this.audience = this.route.parent?.snapshot.paramMap.get('audience') || 
                      this.route.parent?.parent?.snapshot.paramMap.get('audience') ||
                      this.audienceService.audience;
      
      // Extract URL path from route segments (child route wildcard match)
      const childPath = this.route.snapshot.url.map((segment: any) => segment.path).join('/');
      
      // Use the new method that handles homepage vs subpage URL patterns
      this.content = await this.builderService.getPageByUrlWithAudience(childPath, this.audience);

      // Determine site name based on audience
      const siteName = this.audienceService.isHighSchool() ? 'BFW Publishers' : 'Macmillan Learning';

      if (this.content) {
        // Set document title from Builder.io page data
        const pageTitle = this.content.data?.['title'] || this.content.name || siteName;
        this.titleService.setTitle(`${siteName} | ${pageTitle}`);
      } else {
        this.error = 'Page not found';
        this.titleService.setTitle(`${siteName} | Page Not Found`);
      }
    } catch (err) {
      this.error = 'Error loading page';
      const siteName = this.audienceService.isHighSchool() ? 'BFW Publishers' : 'Macmillan Learning';
      this.titleService.setTitle(`${siteName} | Error`);
      console.error('Error fetching Builder content:', err);
    } finally {
      this.loading = false;
    }
  }
}

